import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';

export interface RowActionItem {
  label: string;
  icon?: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
  variant?: 'default' | 'primary' | 'view' | 'edit' | 'danger' | 'success' | 'disabled';
  disabled?: boolean;
  hidden?: boolean;
}

export interface RowActionMenuProps {
  items: (RowActionItem | null | undefined | false)[];
  align?: 'left' | 'right';
  className?: string;
  triggerTitle?: string;
}

export function RowActionMenu({
  items,
  align = 'right',
  className = '',
  triggerTitle = 'Opções',
}: RowActionMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const activeItems = items.filter(
    (item): item is RowActionItem => Boolean(item && typeof item === 'object' && !item.hidden)
  );

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = 190;
    const estimatedHeight = activeItems.length * 40 + 16;

    // Se o espaço abaixo for pequeno na tela, abre para cima
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeAbove = spaceBelow < estimatedHeight + 10 && rect.top > estimatedHeight;

    const top = placeAbove
      ? rect.top - estimatedHeight - 4
      : rect.bottom + 4;

    let left = align === 'right'
      ? rect.right - menuWidth
      : rect.left;

    // Garante que o menu não vaze fora da viewport lateral
    if (left < 8) left = 8;
    if (left + menuWidth > window.innerWidth - 8) {
      left = window.innerWidth - menuWidth - 8;
    }

    setCoords({ top, left });
  };

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();

    function handleClickOutside(event: MouseEvent | TouchEvent) {
      const target = event.target as Node;
      if (
        menuRef.current && !menuRef.current.contains(target) &&
        triggerRef.current && !triggerRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    function handleScrollOrResize() {
      setIsOpen(false);
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen]);

  if (activeItems.length === 0) return null;

  return (
    <div className={`relative inline-block text-left ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors active:scale-95 cursor-pointer bg-transparent border-0 border-none ${
          isOpen
            ? 'text-blue-600 dark:text-blue-400 bg-blue-50/80 dark:bg-blue-950/50'
            : 'text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50/60 dark:hover:bg-blue-950/40'
        }`}
        title={triggerTitle}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <MoreVertical size={18} />
      </button>

      {isOpen && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width: 190,
            zIndex: 999999,
          }}
          className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-xl shadow-[0_12px_40px_rgba(0,0,0,0.22)] py-1.5 flex flex-col gap-0.5 animate-in fade-in zoom-in-95 duration-150 select-none"
          role="menu"
        >
          {activeItems.map((item, idx) => {
            const isDanger = item.variant === 'danger';
            const isSuccess = item.variant === 'success';
            const isDisabledVariant = item.variant === 'disabled';

            // Cores vívidas e contrastadas (azul padrão como solicitado pelo usuário)
            let colorClasses = 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40';
            let iconColorClass = 'text-blue-600 dark:text-blue-400';

            if (isDisabledVariant) {
              colorClasses = 'text-slate-400 dark:text-slate-500 opacity-50 hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-not-allowed';
              iconColorClass = 'text-slate-400 dark:text-slate-500';
            } else if (isDanger) {
              colorClasses = 'text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40';
              iconColorClass = 'text-rose-600 dark:text-rose-400';
            } else if (isSuccess) {
              colorClasses = 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40';
              iconColorClass = 'text-emerald-600 dark:text-emerald-400';
            }

            return (
              <button
                key={idx}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  item.onClick(e);
                }}
                className={`w-full px-3.5 py-2.5 text-xs font-semibold flex items-center gap-2.5 text-left transition-colors border-none cursor-pointer ${
                  item.disabled
                    ? 'opacity-40 cursor-not-allowed text-slate-400'
                    : colorClasses
                }`}
              >
                {item.icon && (
                  <span className={`shrink-0 flex items-center justify-center ${iconColorClass}`}>
                    {item.icon}
                  </span>
                )}
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
